/* eslint-disable camelcase */
import React, {ReactElement, ReactNode, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {
  Button,
  FormControl,
  FormHelperText,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  List,
  ListItem,
  ListItemText,
  Link,
  Grid
} from '@material-ui/core';
import {useForm, Controller, SubmitHandler, FieldError} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
// other imports

// shared imports
import {
  _appointmentPortalLogin,
  _setPatientAppointmentToken
} from '@shared/store/actions/AppointmentSchedularAction';

// scss import
import PaymentsStyledInput from '@components/system/FormInput/PaymentsStyledInput';
import {APP, GET_8_PIXEL_BOX, BLACK_COLOR} from 'src/style/variable';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import NavRoutes from 'src/constants/route/NavRoutes';
import {
  scheduleAppointmentLogin,
  verifyAppointmentPortalToken
} from '@shared/services/AppointmentSchedulingService';

import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import moment from 'moment';
import InitSharedRepo from '../../../../../../shared';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';
import PhoneFormatter from '@components/system/PhoneFormatter/PhoneFormatter';
import HelperUtils from '@shared/utils/HelperUtils';
import {State} from '@shared/store/reducers/Index';
import {environment} from '@shared/environment/environment';
import {CustomRouteComponentProps} from 'src/constants/route/CustomRoute';
import Loader from '@components/framework/loader/Loader';
import PublicPageLayout from '../../../payments/PublicPageLayout/PublicPageLayout';
import {VerifyAppointmentSchedulerTokenResponse} from '@shared/services/service-mappers/AppointmentSchedulingMapper';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginButton: {
      padding: `${GET_8_PIXEL_BOX(1)} 0`,
      fontFamily: APP.FONT_FAMILY.BOLD,
      height: '3.5rem',
      width: '12rem',
      borderRadius: '1.75rem',
      boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px'
    },
    fieldHeight: {
      '& .MuiInputBase-root': {
        height: '3.5rem'
      }
    },
    fieldText: {
      display: 'flex',
      color: BLACK_COLOR,
      paddingBottom: '0.5rem',
      justifyContent: 'flex-start',
      fontFamily: APP.FONT_FAMILY.MEDIUM,
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: '1.5rem'
    },
    patientNote: {
      display: 'flex',
      color: BLACK_COLOR,
      justifyContent: 'flex-start',
      textAlign: 'left',
      marginTop: '3rem',
      fontSize: '1.25rem',
      lineHeight: '1.35rem'
    }
  })
);

interface LogInFormFields {
  guarantor_name: string;
  patient_name: string;
  patient_dob: moment.Moment | null;
}

const LoginFormDefaultValues: LogInFormFields = {
  patient_dob: null,
  patient_name: '',
  guarantor_name: ''
};

const AppointmentLogin = (
  props: CustomRouteComponentProps<{code: string}>
): ReactElement => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState<string>('');
  const {history, match} = props;

  /**
   * Fetching states from Redux
   */
  const isLogin = useSelector((state: State) => {
    return state.appointmentPortal.isLogin;
  });
  const patientAppointmentToken = useSelector((state: State) => {
    return state.appointmentPortal.patientAppointmentToken;
  });
  /**
   * Dispatcher to redux
   */
  const dispatch = useDispatch();
  const [
    apiResponseData,
    setApiResponseData
  ] = useState<VerifyAppointmentSchedulerTokenResponse>();
  const [loginAttempt, setLoginAttempt] = useState<number>(1);
  const [selectedClinicId] = useState<number>(1);

  const [isVerifyingToken, setIsVerifyingToken] = useState(true);

  const redirectToPaymentDashboard = (token?: string) => {
    dispatch(_appointmentPortalLogin(true));

    history.push(
      `${NavRoutes.PUBLIC.APPOINTMENT_PORTAL.HOME}`
      // `/${NavRoutes.PUBLIC.APPOINTMENT_SCHEDULAR_DASHOBARD}/${token}`
    );
  };

  const errorRedirect = React.useCallback((): void => {
    history.push(`/${NavRoutes.PUBLIC.ERROR_PAY}`);
  }, [history]);

  const verifyGuarantorTokenApi = React.useCallback(() => {
    verifyAppointmentPortalToken()
      .then(res => {
        if (res.data) {
          // setting up location to redux state.

          setApiResponseData(res.data);
          setIsVerifyingToken(false);
        }
      })
      .catch(err => {
        if (err) {
          errorRedirect();
        }
      });
  }, [errorRedirect, selectedClinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // setting up token to redux State
    InitSharedRepo.initAfterAuth({token: match?.params?.code});
    dispatch(_setPatientAppointmentToken(match?.params?.code));
    dispatch(_appointmentPortalLogin(false));
    verifyGuarantorTokenApi();
    if (isLogin) {
      redirectToPaymentDashboard(patientAppointmentToken);
    }
  }, [selectedClinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const {control, handleSubmit, errors} = useForm<LogInFormFields>({
    context: undefined,
    reValidateMode: undefined,
    shouldFocusError: false,
    shouldUnregister: false,
    defaultValues: LoginFormDefaultValues,
    criteriaMode: undefined,
    mode: 'all',

    resolver: async function (values) {
      // TODO: Add types for errorsObj
      const errorsObj: any = {};
      if (!values.guarantor_name) {
        errorsObj.guarantor_name = {
          message:
            'APPOINTMENTS_SCHEDULAR_PORTAL.VALIDATIONS.PATIENT_LAST_NAME_REQUIRED'
        };
      }
      if (!values.patient_name) {
        errorsObj.patient_name = {
          message: 'PAYMENTS_LOGIN.VALIDATIONS.PATIENTS_NAME'
        };
      }
      if (!values.patient_dob) {
        errorsObj.patient_dob = {
          message: 'PAYMENTS_LOGIN.VALIDATIONS.PATIENTS_DOB'
        };
      } else if (
        !values.patient_dob.isValid() ||
        values.patient_dob.format('YYYY') < '1900' ||
        values.patient_dob.format('YYYY') > '2099'
      ) {
        errorsObj.patient_dob = {
          message: 'VALIDATORS.INVALID_DATE'
        };
      }
      return {values, errors: errorsObj};
    }
  });

  // Synchronous validation is being implemented here
  const handleFormSubmit: SubmitHandler<{
    guarantor_name: string;
    patient_name: string;
    patient_dob: moment.Moment;
  }> = async values => {
    setSubmitLoader(true);
    setLoginErrorMessage('');
    scheduleAppointmentLogin({
      first_name: values.patient_name,
      dob: values.patient_dob.format('YYYY-MM-DD'),
      last_name: values.guarantor_name
    })
      .then(res => {
        if (res?.data?.token) {
          InitSharedRepo.initAfterAuth({token: res.data.token});
          dispatch(_setPatientAppointmentToken(res.data.token));
          redirectToPaymentDashboard(match.params.code);
        }
      })
      .catch(err => {
        setLoginErrorMessage(err.message || t('APP.ERROR'));
        setSubmitLoader(false);
        setLoginAttempt(loginAttempt + 1);
      });
  };

  const renderPortalHosTitle = React.useCallback(() => {
    if (apiResponseData?.clinic?.name) {
      return apiResponseData.clinic.name;
    }
    return apiResponseData?.group?.group_name;
  }, [apiResponseData]);

  const getSupportEmail = React.useCallback(() => {
    let email = null;
    if (
      apiResponseData?.group?.patients_shared === true &&
      apiResponseData?.group?.contact_email
    ) {
      email = apiResponseData.group.contact_email;
    } else if (apiResponseData?.clinic?.id && apiResponseData?.clinic?.contact_email) {
      email = apiResponseData.clinic.contact_email;
    } else {
      email = environment.supportEmail;
    }
    return email;
  }, [apiResponseData]);

  const renderAddressLine1 = React.useCallback(() => {
    if (apiResponseData?.clinic?.id) {
      return HelperUtils.formatFullAddress({
        line1: apiResponseData.clinic?.address,
        line2: apiResponseData.clinic?.address_line_2 || '',
        city: '',
        state: '',
        country: '',
        zip: ''
      });
    }
    return HelperUtils.formatFullAddress({
      line1: apiResponseData?.group?.address,
      line2: apiResponseData?.group?.address_line_2 || '',
      city: '',
      state: '',
      country: '',
      zip: ''
    });
  }, [apiResponseData]);

  const renderAddressLine2 = React.useCallback(() => {
    if (apiResponseData?.clinic?.id) {
      return HelperUtils.formatFullAddress({
        line1: '',
        line2: '',
        city: apiResponseData?.clinic?.city,
        state: apiResponseData?.clinic?.state,
        country: '',
        zip: (apiResponseData?.clinic?.zip || '').toString()
      });
    }
    return HelperUtils.formatFullAddress({
      line1: '',
      line2: '',
      city: apiResponseData?.group?.city || '',
      state: apiResponseData?.group?.state || '',
      zip: (apiResponseData?.group?.zip || '').toString()
    });
  }, [apiResponseData]);

  const renderPracticePhones = (): ReactNode => {
    const clinicDetails = apiResponseData?.clinic;
    if (!clinicDetails) {
      return null;
    }

    const formatPhone = (phone: string): string =>
      phone.startsWith('+1') ? phone : `+1${phone}`;

    if (clinicDetails.phone === clinicDetails.billing_phone) {
      return clinicDetails.phone ? (
        <div className="clinic-phone">
          {t('PAYMENTS_LOGIN.PHONE_QUERY_LABEL')}
          <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
        </div>
      ) : null;
    }

    return (
      <>
        {clinicDetails.phone ? (
          <div className="clinic-phone">
            {t('PAYMENTS_LOGIN.PHONE')}
            <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
          </div>
        ) : null}
        {clinicDetails.billing_phone ? (
          <div className="clinic-phone">
            {t('PAYMENTS_LOGIN.BILLING_PHONE')}
            <PhoneFormatter value={formatPhone(clinicDetails.billing_phone)} />
          </div>
        ) : null}
      </>
    );
  };

  const renderAfterContent = (): ReactElement => (
    <div className="payment-login-after-content">
      <section className="flex-centering contact-section">
        <p className="contact-us-text">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.CONTACT_INFO"
            values={{
              supportEmail: getSupportEmail()
            }}
            components={[
              <Link href={`mailto:${getSupportEmail()}`} key="support-email" />
            ]}
          />
        </p>
      </section>

      <div className="tag-wrapper flex-centering">
        <p className="tag-text">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.APP_CLOSING_INFORMATION_PART1"
            components={[<span key="closing-information" />]}
          />
        </p>
        <p className="tag-text-2">
          {t('GUARANTOR_PORTAL.LOGIN_SCREEN.APP_CLOSING_INFORMATION_PART2')}
        </p>
        <Link href={`${environment.websiteDetails.name}`} target="pcl">
          <MedopsLogo className="logo" />
        </Link>
      </div>
    </div>
  );

  const renderInformation = (): JSX.Element => {
    return (
      <Grid className="helper-information">
        <Typography component={'h1'} className="title">
          <Trans
            i18nKey="APPOINTMENTS_SCHEDULAR_PORTAL.LOGIN_SCREEN.HELPER_TEXT_TITLE"
            components={[<span className="title-highlight" key="highlight" />]}
          />
        </Typography>
        <List component={'ol'} disablePadding>
          <ListItem disableGutters>
            <ListItemText
              primary={
                <Typography className="info-text">
                  1.&nbsp;
                  {t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.LOGIN_SCREEN.HELPER_TEXT_BULLET_1'
                  )}
                </Typography>
              }
            />
          </ListItem>
          <ListItem disableGutters>
            <ListItemText
              primary={
                <Typography className="info-text">
                  2.&nbsp;
                  {t(
                    'APPOINTMENTS_SCHEDULAR_PORTAL.LOGIN_SCREEN.HELPER_TEXT_BULLET_2'
                  )}
                </Typography>
              }
            />
          </ListItem>
        </List>
      </Grid>
    );
  };

  const renderLoginForm = (): JSX.Element => {
    return (
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className={classes.fieldText}>
          {t('PAYMENTS_LOGIN.GUARANTOR_NAME')}*
        </div>
        <FormControl
          error={!!errors.guarantor_name}
          fullWidth={true}
          variant="outlined">
          <Controller
            id="guarantor_name"
            as={PaymentsStyledInput}
            control={control}
            name={'guarantor_name'}
            type={'text'}
            value={''}
            placeholder={t('PAYMENTS_LOGIN.PLACEHOLDERS.PATIENT_LAST_NAME')}
            defaultValue=""
            error={!!errors.guarantor_name}
            style={{height: '3.5rem'}}
          />
          {errors.guarantor_name && (
            <FormHelperText>
              {t(errors?.guarantor_name?.message || '')}
            </FormHelperText>
          )}
        </FormControl>
        <div className={classes.fieldText}>
          {t('PAYMENTS_LOGIN.PATIENTS_NAME')}*
        </div>
        <FormControl error={!!errors.patient_name} fullWidth variant="outlined">
          <Controller
            id="patient_name"
            as={PaymentsStyledInput}
            control={control}
            name={'patient_name'}
            type={'text'}
            value={''}
            placeholder={t('PAYMENTS_LOGIN.PLACEHOLDERS.PATIENTS_NAME')}
            defaultValue=""
            error={!!errors.patient_name}
            style={{height: '3.5rem'}}
          />
          {errors.patient_name && (
            <FormHelperText>
              {t(errors?.patient_name?.message || '')}
            </FormHelperText>
          )}
        </FormControl>
        <div className={classes.fieldText}>
          {t('PAYMENTS_LOGIN.PATIENTS_DOB')}*
        </div>
        <FormControl error={!!errors.patient_dob} fullWidth variant="outlined">
          <Controller
            id="patient_date_of_birth"
            render={({onChange, onBlur, value}): React.ReactElement => (
              <DateTimePickerWrapper
                className={classes.fieldHeight}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={t('PAYMENTS_LOGIN.PLACEHOLDERS.PATIENTS_DOB')}
                aria-placeholder={'Patients date of birth'}
                error={!!errors?.patient_dob}
              />
            )}
            control={control}
            name={'patient_dob'}
          />
          {errors.patient_dob && (
            <FormHelperText>
              {t((errors.patient_dob as unknown as FieldError).message || '')}
            </FormHelperText>
          )}
        </FormControl>

        {loginErrorMessage != '' && (
          <div className="error-message">{loginErrorMessage}</div>
        )}
        <div className="flex-centering">
          <Button
            variant="contained"
            size={'medium'}
            color="primary"
            className={[classes.loginButton].join(' ')}
            type={'submit'}
            onClick={handleSubmit(handleFormSubmit)}>
            {submitLoader ? <ButtonLoader /> : 'SUBMIT'}
          </Button>
        </div>

        <div className={[classes.patientNote, 'flex-centering'].join(' ')}>
          {apiResponseData?.clinic?.clinic_contact_text ||
            t('ADD_CLINIC.DEFAULT_VALUE.CLINIC_CONTACT_TEXT')}
        </div>
      </form>
    );
  };

  if (isVerifyingToken === true) {
    return (
      <div className="flex-centering payment-login-loader">
        <Loader size={56} color="primary" />
      </div>
    );
  }

  return (
    <PublicPageLayout
      className="payment-login-page"
      testId="pcl-appointment-loginPage"
      showHero={false}
      contentLayout="card"
      practiceBadgeLabel={renderPortalHosTitle()}
      practiceName={renderPortalHosTitle()}
      addressLine1={renderAddressLine1()}
      addressLine2={renderAddressLine2()}
      practicePhones={renderPracticePhones()}
      afterContent={renderAfterContent()}>
      <div className="panel helper-information-panel">{renderInformation()}</div>
      <div className="panel login-panel">{renderLoginForm()}</div>
    </PublicPageLayout>
  );
};

export default AppointmentLogin;
